import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const DISMISS_KEY = 'looseEndsDismissedOn';

// The dismissal lasts the rest of the day: we store the day it was dismissed and
// compare against today, so the banner returns on its own tomorrow. localStorage
// throws in some privacy modes, so every access is guarded — a reminder is not
// worth breaking the dashboard over.
const readDismissedOn = () => {
    try {
        return window.localStorage.getItem(DISMISS_KEY);
    } catch {
        return null;
    }
};

const writeDismissedOn = (day) => {
    try {
        window.localStorage.setItem(DISMISS_KEY, day);
    } catch {
        // Dismissal just won't survive a reload; the banner still closes now.
    }
};

const localToday = () => {
    const d = new Date();
    return [d.getFullYear(), String(d.getMonth() + 1).padStart(2, '0'), String(d.getDate()).padStart(2, '0')].join('-');
};

const formatDay = (isoDay) => {
    if (!isoDay) return '';
    const [y, m, d] = isoDay.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' });
};

/**
 * Daily summary of what the previous day left open — dogs never checked out,
 * appointments never closed, trips never finished, groomings never completed.
 *
 * Covers that one day only, by design: a cumulative count of everything ever
 * left unclosed goes stale and stops being read.
 */
export default function LooseEndsBanner({ summary }) {
    // Today per the server when available, so the dismissal boundary matches the
    // day the counts describe.
    const today = summary?.today || localToday();
    const [dismissed, setDismissed] = useState(() => readDismissedOn() === today);

    if (!summary || !Array.isArray(summary.categories) || dismissed) return null;

    const pending = summary.categories.filter(c => c.count > 0);

    const dismiss = () => {
        writeDismissedOn(today);
        setDismissed(true);
    };

    if (pending.length === 0) {
        return (
            <div className="loose-ends loose-ends--clear">
                <span className="loose-ends-check">✅</span>
                <span>Ayer quedó todo cerrado. Sin pendientes.</span>
                <button type="button" className="loose-ends-dismiss" onClick={dismiss} aria-label="Ocultar por hoy">×</button>
            </div>
        );
    }

    return (
        <div className="loose-ends">
            <div className="loose-ends-head">
                <span className="loose-ends-title">
                    📋 {summary.total} pendiente{summary.total === 1 ? '' : 's'} de ayer
                </span>
                <span className="loose-ends-window">{formatDay(summary.date)}</span>
                <button type="button" className="loose-ends-dismiss" onClick={dismiss} aria-label="Ocultar por hoy">×</button>
            </div>

            <div className="loose-ends-list">
                {pending.map(c => (
                    <Link key={c.key} to={c.route} className="loose-end-item" title={c.detail}>
                        <span className="loose-end-count">{c.count}</span>
                        <span className="loose-end-label">{c.label}</span>
                        <span className="loose-end-go">→</span>
                    </Link>
                ))}
            </div>
        </div>
    );
}
